import React, { useEffect, useState, useCallback, useRef } from "react";
import Layout4 from "../../DataLayouts/Layout4";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import PersonIcon from "@mui/icons-material/Person";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import CategoryIcon from "@mui/icons-material/Category";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { MAIN_URL } from "../../../Configurations/Urls";
import { fetchAttendanceRecord } from "../../../Apis/Attendance";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import {
  applyClientToolbarFilters,
  mergeToolbarFilters,
  ToolbarDateFilter,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters";

const MODULE = "employee_attendance_records";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "attendance_date",
  sortOrder: "desc",
};

const ATTENDANCE_RECORD_TOOLBAR_FILTERS = [
  {
    urlKey: "attendance_date",
    field: "attendance_date",
    operator: "date_equals",
  },
];

const formatDateTime12Hour = (dateTime) => {
  if (!dateTime || dateTime === "-") return "";
  const date = new Date(dateTime);
  if (Number.isNaN(date.getTime())) return dateTime;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${year}-${month}-${day} ${time}`;
};

// Helper functions
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  return filters;
};

function AttendanceRecordList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userData } = useAuthStore();
  const org = userData?.organization;

  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState([]);
  const [tableHeaders, setTableHeaders] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);

  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams);

  // -----------------------------------
  // DELETE ATTENDANCE RECORD
  // -----------------------------------
  const deleteRecord = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/attendance-employee-record/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        if (response.ok) {
          toast.success(data.message || "Record deleted successfully");
          // Refresh data after deletion by triggering useEffect
          setSearchParams((prev) => new URLSearchParams(prev));
        } else {
          const status = response.status;
          if (status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else if (status === 422) {
            const firstError =
              data.errors?.[Object.keys(data.errors || {})[0]]?.[0] ||
              "Validation failed.";
            toast.error(firstError);
          } else if (status === 409) {
            toast.error(data.error || "This record is linked to other records.");
          } else {
            toast.error(data.message || data.error || "Failed to delete record");
          }
        }
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          toast.error(error.response?.data?.error || "Failed to delete record");
        }
        console.error("Delete failed:", error);
      }
    },
    [org?.organization_id, setSearchParams]
  );

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // -----------------------------------------------
  // LOAD COLUMNS FROM METADATA (no hardcoded columns)
  // -----------------------------------------------
  useEffect(() => {
    const loadConfig = async () => {
      if (!org?.organization_id) return setLoadingConfig(false);

      try {
        const metaRes = await fetch(`${MAIN_URL}/api/metadata/${MODULE}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (metaRes.ok) {
          const meta = await metaRes.json();
          const fields = meta.fields || [];
          const displayFields = fields.filter((f) => f.is_display_field);
          const useFields = displayFields.length > 0 ? displayFields : fields;
          const cols = useFields
            .filter((f) => f.field_key !== "organization_person_id")
            .map((f, i) => ({
              field: f.field_key,
              label: f.label || f.field_key,
              visible: true,
              sortable: true,
              filterable: !!f.is_filterable,
              width: 150,
              field_type: f.field_type,
            }));
          setConfigColumns(cols);
          setTableHeaders(cols.map((c) => ({ name: c.label, value_key: c.field })));
        }
        const req = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const res = await req.json();
        const dg = res.datagrids || [];
        const key = `organization_person_attendance_records_grid_${org.organization_id}`;
        const saved = dg.find((x) => x.datagrid_key === key);
        if (saved?.datagrid_default_configuration) {
          setTableConfig(saved.datagrid_default_configuration);
          if (saved.datagrid_default_configuration.columns?.length > 0) {
            const savedCols = saved.datagrid_default_configuration.columns.filter(
              (c) => (c.field || c.key) !== "organization_person_id"
            );
            setConfigColumns((prev) => (prev.length ? prev : savedCols));
            setTableHeaders((prev) =>
              prev.length ? prev : savedCols.map((c) => ({ name: c.label || c.field, value_key: c.field || c.key }))
            );
          }
        }

        const settlementCol = {
          field: "settlement_status",
          label: "Settlement Status",
          visible: true,
          sortable: false,
          filterable: false,
          width: 150,
        };
        setConfigColumns((prev) =>
          prev.some((c) => (c.field || c.key) === "settlement_status")
            ? prev
            : [...prev, settlementCol]
        );
        setTableHeaders((prev) =>
          prev.some((h) => h.value_key === "settlement_status")
            ? prev
            : [...prev, { name: "Settlement Status", value_key: "settlement_status" }]
        );
      } catch (err) {
        console.error("Config load error:", err);
      }

      setLoadingConfig(false);
    };

    loadConfig();
  }, [org]);

  // -----------------------------------------------
  // Load records on mount
  // -----------------------------------------------
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = mergeToolbarFilters(
          buildFiltersFromParams(searchParams),
          searchParams,
          ATTENDANCE_RECORD_TOOLBAR_FILTERS
        );
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_person_attendance_record_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/attendance-records-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          const res = await fetchAttendanceRecord(org.organization_id);
          data = applyClientToolbarFilters(
            res?.attendance_records?.data || res?.attendance_records || [],
            searchParams,
            ATTENDANCE_RECORD_TOOLBAR_FILTERS
          );
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const displayName = (item) =>
          item?.full_name ?? item?.employee_name ?? item?.Employee ?? item?.employee ?? "";

        const formatMinutes = (value) => {
          if (value === null || value === undefined || value === "") return "";
          const total = Number(value);
          if (Number.isNaN(total)) return `${value}`;
          const sign = total < 0 ? "-" : "";
          const abs = Math.abs(Math.round(total));
          const hours = Math.floor(abs / 60);
          const minutes = abs % 60;
          if (hours === 0) return `${sign}${minutes}m`;
          if (minutes === 0) return `${sign}${hours}h`;
          return `${sign}${hours}h ${minutes}m`;
        };

        setRows(
          data.map((item) => {
            const remarks = item?.remarks ?? "";
            const isSettled = String(remarks)
              .toLowerCase()
              .includes("settled via attendance settlement");

            return {
              ...item,
              id: item?.organization_person_attendance_record_id ?? item?.id,
              full_name: displayName(item),
              employee_name: displayName(item),
              employee: displayName(item),
              Employee: displayName(item),
              profile_image_url:
                item?.profile_image_url ||
                item?.person?.profile_image_url ||
                "",
              attendance_status_type_name:
                item?.attendance_status_type_name ?? item?.attendance_status_type ?? "",
              clock_in_time: formatDateTime12Hour(item?.clock_in_time),
              clock_out_time: formatDateTime12Hour(item?.clock_out_time),
              workshift_total_work_minutes: formatMinutes(
                item?.workshift_total_work_minutes
              ),
              actual_total_work_minutes: formatMinutes(
                item?.actual_total_work_minutes
              ),
              workshift_total_break_minutes: formatMinutes(
                item?.workshift_total_break_minutes
              ),
              actual_total_break_minutes: formatMinutes(
                item?.actual_total_break_minutes
              ),
              overtime_minutes: formatMinutes(item?.overtime_minutes),
              remarks,
              settlement_status: isSettled ? "Settled" : "",
            };
          })
        );
      } catch (err) {
        if (err.name === "AbortError") return;
        
        if (err.response && err.response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          console.error("Failed to load attendance records", err);
          toast.error("Failed to load attendance records");
          setRows([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org, searchParams]);

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Employees Attendance Records"}
        btnName={null}
        Data={rows}
        add_action={"ATTENDANCE_RECORD_ADD"}
        delete_action={"ATTENDANCE_RECORD_DELETE"}
        tableHeaders={tableHeaders}
        Icons={[
          <PersonIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <FormatAlignJustifyIcon color="primary" />,
          <CategoryIcon sx={{ color: "text.secondary" }} />,
          <DateRangeIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Employee Attendance Record",
          "Employee Attendance Record",
          "Add Employee Attendance Record",
          "Employee Attendance Record & related logs will be deleted permanently.",
        ]}
        Route={"/attendance/employee-records"}
        setData={setRows}
        DeleteFunc={deleteRecord}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="organization_person_attendance_records"
          primaryKey="organization_person_attendance_record_id"
          heading="Attendance Records"
          data={rows}
          sortname="attendance_date"
          showActions={false}
          Route="/attendance/employee-records"
          onSortChange={handleSortChange}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule={MODULE}
          toolbarExtras={
            <ToolbarDateFilter
              label="Date"
              urlKey="attendance_date"
              searchParams={searchParams}
              onChange={handleToolbarFilterChange}
            />
          }
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default AttendanceRecordList;









